import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import type { ShipmentRow } from '#/entities/shipment'
import { LINE_CAPABILITY, useDefaultLine } from '#/entities/telephony'
import type { MassCallStartItem } from '#/shared/api/generated/communication/models'
import { useStartMassCallApiV1SendMassCallPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { activeMassJobsQueryKey } from '#/shared/lib/mass-jobs'
import {
  PHONE_STATE,
  connectTelephony,
  isLiveSession,
  useTelephonyStore,
} from '#/shared/lib/telephony'
import { toast } from '#/shared/ui/sonner'

export interface UseMassCallActionOptions {
  /** Called after the dial session was accepted by the backend. */
  onStarted?: () => void
}

export interface UseMassCallActionResult {
  startAutoDial: () => void
  isStarting: boolean
  /** Translated reason the dial can't start right now, or null when it can. */
  disabledReason: string | null
}

function toCallItems(
  rows: ShipmentRow[],
  selectedIds: string[],
): MassCallStartItem[] {
  const selected = new Set(selectedIds)
  return rows
    .filter((row) => selected.has(row.id) && row.customerPhone)
    .map((row) => ({ shipment_id: row.shipmentId, phone: row.customerPhone }))
}

function hasCallRecipients(
  rows: ShipmentRow[],
  selectedIds: string[],
): boolean {
  const selected = new Set(selectedIds)
  return rows.some((row) => selected.has(row.id) && row.customerPhone)
}

interface DisabledReasonInput {
  isStarting: boolean
  onRegularCall: boolean
  connecting: boolean
  hasRecipients: boolean
  /** True once lines have loaded and none of them can place calls. */
  noCallLine: boolean
}

/** Every condition that blocks the start, in the order the user should fix
 *  them. The button renders the returned key as a tooltip so no blocked click
 *  is ever silent. */
function disabledReasonKey(input: DisabledReasonInput): string | null {
  if (input.isStarting) return 'massCall.disabled.starting'
  if (input.onRegularCall) return 'massCall.disabled.onCall'
  if (input.connecting) return 'massCall.disabled.connecting'
  if (!input.hasRecipients) return 'massCall.disabled.noRecipients'
  if (input.noCallLine) return 'massCall.disabled.noLine'
  return null
}

/**
 * Auto Dial mass action: starts a backend power-dial session
 * (`POST /send/mass/call`) over the selected rows from the default
 * call-capable line. Progress then arrives as a `call` job on the unified
 * mass-jobs list, which the header dial pill renders.
 */
export function useMassCallAction(
  rows: ShipmentRow[],
  selectedIds: string[],
  options: UseMassCallActionOptions = {},
): UseMassCallActionResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const startMutation = useStartMassCallApiV1SendMassCallPost()
  // Same default-line resolution as single calls and mass SMS, restricted to
  // call-capable lines; fetched lazily once the user selects rows.
  const { line, isLoading: linesLoading } = useDefaultLine({
    capability: LINE_CAPABILITY.call,
    enabled: selectedIds.length > 0,
  })
  const phoneStatus = useTelephonyStore((s) => s.status)
  const onRegularCall = useTelephonyStore((s) =>
    s.sessions.some((session) => isLiveSession(session) && !session.powerdial),
  )

  const reasonKey = disabledReasonKey({
    isStarting: startMutation.isPending,
    onRegularCall,
    connecting: phoneStatus === PHONE_STATE.connecting,
    hasRecipients: hasCallRecipients(rows, selectedIds),
    noCallLine: !linesLoading && !line,
  })

  const start = async (items: MassCallStartItem[]) => {
    try {
      await startMutation.mutateAsync({
        data: { items },
      })
      await queryClient.invalidateQueries({
        queryKey: activeMassJobsQueryKey(),
      })
      options.onStarted?.()
    } catch (error) {
      toast.error(getApiErrorDetail(error) ?? t('massCall.toast.error'))
    }
  }

  const startAutoDial = (): void => {
    const telephony = useTelephonyStore.getState()
    // The bridge lands on the registered WebRTC softphone — without it the
    // agent leg has nowhere to ring and the customer is left hanging. Kick a
    // connect and ask the user to retry once it's up.
    if (telephony.status !== PHONE_STATE.ready) {
      toast.info(t('massCall.toast.softphoneConnecting'))
      void connectTelephony()
      return
    }
    // A live regular call would get a second call bridged on top — surface the
    // ongoing call instead. The dialer's own bridge leg is fine: starting again
    // mid-session just appends the new items to the running queue.
    if (
      telephony.sessions.some(
        (session) => isLiveSession(session) && !session.powerdial,
      )
    ) {
      toast.info(t('massCall.toast.onCall'))
      telephony.requestAttention()
      return
    }
    const items = toCallItems(rows, selectedIds)
    if (items.length === 0) {
      toast.error(t('massCall.toast.noRecipients'))
      return
    }
    if (!line) {
      // Distinguish "lines still loading" from a real capability gap so the
      // user isn't told they lack a line while the fetch is in flight.
      if (linesLoading) {
        toast.info(t('massCall.toast.linesLoading'))
        return
      }
      toast.error(t('massCall.toast.noLine'))
      return
    }
    void start(items)
  }

  return {
    startAutoDial,
    isStarting: startMutation.isPending,
    disabledReason: reasonKey ? t(reasonKey) : null,
  }
}
